import React from "react";
import s from "./ScoresReport.module.scss";
import ScoreItem from "../ScoreItem";
import { ReportRiskValue } from "./ReportRiskValue";
import { ReportCTA } from "./ReportCTA";
import {
  findPlaceFromQuery,
  fetchScores,
} from "../../services/placeSearchService";
import {
  extractNeededScores,
  formatScore,
  formatSortScores,
} from "../../utils/utils";
import { Score, scores_types } from "../../utils/types";
import LoadingElement from "../LoadingElement";

type State = {
  address: string;
  message: string;
  scores: Score[];
};

export const ScoresReport: React.FC<{ address: string }> = ({ address }) => {
  const [state, setState] = React.useState<State>({
    address,
    scores: [],
    message: "Fetching Location...",
  });

  const [climateScore, setClimateScore] = React.useState<Score>();

  const updateMessage = (message: string) => setState({ ...state, message });

  React.useEffect(() => {
    // Find the Place by address
    findPlaceFromQuery(address)
      .then(async (data) => {
        if (typeof data === "string") {
          updateMessage(data);
        }

        const { place_name, geometry } = data;

        const [lng, lat] = geometry.coordinates;

        if (!lat || !lng) {
          updateMessage(
            "Missing location fields, cannot calculate score, please try another address"
          );
          return;
        }

        const result = await fetchScores(lat, lng);
        // An unhandled error thrown at api.climatecheck.com will return a
        // hardcoded string in src/services/googleMapsService.ts
        // for example an address that doesn't exists (maybe)
        if (typeof result === "string") {
          updateMessage(result);
          return;
        }

        const response = await result?.json();
        // A "controlled" error returned by api.climatecheck.com
        // for example an address that is not in the US
        if (response.Error) {
          updateMessage(response.Error);
          return;
        }

        const { ClimateRisk, ...scoresValues } = extractNeededScores(response);

        const cs = formatScore(
          scores_types.ClimateRisk,
          response[scores_types.ClimateRisk]
        );

        setClimateScore(cs);

        const formattedScores = formatSortScores(scoresValues);

        setState({
          address: place_name || "",
          scores: formattedScores,
          message: "",
        });
      })
      .catch((e) => {
        updateMessage(
          "An unkown error ocurred, please contact climatecheck.com"
        );
      });
    // eslint-disable-next-line
  }, []);

  if (state.message) {
    return <LoadingElement text={state.message} />;
  }

  const [first] = state.scores;
  const last = state.scores[state.scores.length - 1];

  return (
    <>
      <div className={s.widgetOverline}>
        <p className={s.widgetOverlineTitle}>
          How will climate change affect your home?
        </p>
        <p className={s.widgetOverlineAddress}>{address}</p>
      </div>
      <ReportRiskValue score={climateScore} first={first} last={last} />
      <div className={s.widgetDivider}></div>
      <div className={s.widgetScores}>
        <ul>
          {state.scores.map((score, i) => (
            <ScoreItem score={score} key={i} />
          ))}
        </ul>
      </div>
      <ReportCTA address={address} />
    </>
  );
};
